import { AxiosResponse } from 'axios';
import { PropertyItem, PropertyListResponse } from '../../../models';
import { AuditLogEntry } from '../models/audit-log';
import { UrlItem } from '../models/source';
import { propertiesApi } from './api';

export interface PropertyDetailResponse {
  property: PropertyItem;
  urlItem: UrlItem | null;
  auditLogs: AuditLogEntry[];
}

/**
 * Get all properties with pagination
 * @param limit Number of items to return
 * @param nextToken Pagination token
 * @returns List of properties and next token
 */
export const getAllProperties = async (
  limit = 20,
  nextToken?: string
): Promise<PropertyListResponse> => {
  const params = {
    limit,
    ...(nextToken && { nextToken }),
  };

  const response: AxiosResponse<PropertyListResponse> =
    await propertiesApi.getAll(params);
  return response.data;
};

export const getAllPropertiesAllPages = async (
  pageSize = 200
): Promise<PropertyListResponse> => {
  const aggregated: PropertyItem[] = [];
  let nextToken: string | undefined;

  do {
    const response = await getAllProperties(pageSize, nextToken);
    aggregated.push(...response.items);
    nextToken = response.nextToken;
  } while (nextToken);

  return { items: aggregated };
};

/**
 * Get a property by ID and source ID
 * @param id Property ID
 * @param sourceId Source ID
 * @returns The property detail response with property, urlItem, and auditLogs
 */
export const getProperty = async (
  id: string,
  sourceId: string
): Promise<PropertyDetailResponse> => {
  const response: AxiosResponse<PropertyDetailResponse> =
    await propertiesApi.getByIdAndSource(id, sourceId);
  return response.data;
};

/**
 * Get property counts (active, inactive, total)
 * @returns Property counts
 */
export const getPropertyCount = async (): Promise<{ active: number; inactive: number; total: number }> => {
  const response: AxiosResponse<{ active: number; inactive: number; total: number }> =
    await propertiesApi.getCount();
  return response.data;
};

/**
 * Recalculate property counts
 * @returns Updated property counts
 */
export const recalculatePropertyCounts = async (): Promise<{ message: string; counts: { active: number; inactive: number; total: number } }> => {
  const response: AxiosResponse<{ message: string; counts: { active: number; inactive: number; total: number } }> =
    await propertiesApi.recalculateCounts();
  return response.data;
};

/**
 * Update a property
 * @param id Property ID
 * @param sourceId Source ID
 * @param updates Property updates
 * @returns Updated property
 */
export const updateProperty = async (
  id: string,
  sourceId: string,
  updates: Partial<PropertyItem>
): Promise<PropertyItem> => {
  const response: AxiosResponse<{ property: PropertyItem }> =
    await propertiesApi.update(id, sourceId, updates);
  return response.data.property;
};
